import React, { createContext, useContext, useEffect, useRef } from 'react';
import mixpanel, { OverridedMixpanel } from 'mixpanel-browser';
import mixpanelConfig from '../../config/mixpanel.config';
import { ENVIRONMENT } from '../../config/environment';
import { mixpanelEventNameMapping, mixpanelPropertyNameModel } from '../../models/Mixpanel.model';
import { useRole } from '../user/RoleProvider.client';
import { AuthSessionUser } from '@lib/home-wizard/services';

declare global {
    interface Window {
        trackFormStep: (data: any) => void;
    }
}

interface Props {
    children: React.ReactElement;
    filterCurrentUser: boolean;
    user?: AuthSessionUser;
}

const MixpanelContext = createContext<{ mixpanel?: OverridedMixpanel }>({});

export const MixpanelProvider = ({ children, filterCurrentUser, user }: Props) => {
    if (ENVIRONMENT !== 'production') {
        // eslint-disable-next-line no-console
        console.log('Rendering Mixpanel in mode:', ENVIRONMENT);
    }

    const init = useRef(false);
    const { role } = useRole();
    // TODO: fix the mobile cutoff, use tailwind config
    const MOBILE_BREAKPOINT = 1024;

    useEffect(() => {
        if (init.current) {
            return;
        }

        const { token, debug } = mixpanelConfig;

        // always initialize mixpanel, or else we cannot disable it
        mixpanel.init(token, {
            debug,
            ignore_dnt: true,
            loaded: (mixpanel) => {
                if (user?.id) {
                    mixpanel.identify(user.id);
                    mixpanel.people.set('$name', `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`);
                    mixpanel.people.set('$id', user.id);
                    mixpanel.people.set('$email', user.email);
                    if (user.attributes?.debtorId) {
                        mixpanel.people.set('DebtorId', user.attributes.debtorId);
                    }
                }
                if (debug) {
                    // eslint-disable-next-line no-console
                    console.info('Mixpanel has been initialized with distinct id:', mixpanel.get_distinct_id());
                }
                init.current = true;
            },
        });

        // allow the mixpanel tracking to be bound, so we can call it separately if needed, without losing the default properties
        const originalTrack = mixpanel.track;
        mixpanel.track = function track(name, properties, ...rest) {
            if (window.__METIS_DISABLE_MIXPANEL__) return;

            const distinctId = user?.id ?? mixpanel.get_distinct_id();

            const mappedProperties = {};

            for (const k in properties) {
                const mappedKey = mixpanelPropertyNameModel[k] ?? k;
                mappedProperties[mappedKey] = properties[k];
            }

            const props = {
                device: window.innerWidth < MOBILE_BREAKPOINT ? 'Mobile' : 'Desktop',
                'user-role': user?.role ?? role ?? localStorage.getItem('user') ?? 'unknown',
                debtorId: user?.attributes?.debtorId ?? undefined,
                distinct_id: distinctId,
                page: window.location.pathname,
                $user_id: user?.id,
                user_id: user?.id,
                ...mappedProperties,
            };

            const mappedName = mixpanelEventNameMapping[name] ?? name;

            originalTrack.call(mixpanel, mappedName, props, ...rest);

            if (typeof window.trackFormStep !== 'undefined') {
                window.trackFormStep({
                    form_name: 'expert_skantrae',
                    event_type: 'interaction',
                    step_name: mappedName,
                    delta: [],
                    form: props,
                });
            }
        };

        if (filterCurrentUser || window.__METIS_DISABLE_MIXPANEL__) {
            if (debug) {
                // eslint-disable-next-line no-console
                console.info('Mixpanel has been disabled because a filtered IP was detected');
            }
            mixpanel.disable();
        }

        mixpanel.track('Page View', {
            page: window.location.pathname,
        });
    }, [user]);

    return <MixpanelContext.Provider value={{ mixpanel }}>{children}</MixpanelContext.Provider>;
};

export const useMixpanel = () => useContext(MixpanelContext);
